import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

import type { ProfileLanguageInput } from '@xing-com/crate-common-graphql-types';
import type { ApplyButtonPreScreeningLanguageFragment } from '@xing-com/crate-jobs-graphql';
import { UpdateLanguagesAndWorkPermitDocument } from '@xing-com/crate-jobs-graphql';

import type {
  LanguageQuestionId,
  PreScreeningAnswers,
} from './pre-screening-dialog.constants';

type Props = {
  continueApplyFlow: () => void;
  profileLanguages: ApplyButtonPreScreeningLanguageFragment[];
  setShowPreScreeningDialog: (show: boolean) => void;
  showWorkPermitQuestion: boolean;
  viewerId?: string;
  visibleLanguageQuestionIds: LanguageQuestionId[];
};

const languageQuestionData: Record<
  LanguageQuestionId,
  { identifier: string; text: string }
> = {
  englishSkills: {
    identifier: 'en',
    text: 'English',
  },
  germanSkills: {
    identifier: 'de',
    text: 'German',
  },
};

const toLanguageInput = (
  language: ApplyButtonPreScreeningLanguageFragment
): ProfileLanguageInput => ({
  identifier: language.identifier,
  skillLevelId: language.skillLevelId,
  text: language.text,
});

export const useConfirmPreScreening = ({
  continueApplyFlow,
  profileLanguages,
  setShowPreScreeningDialog,
  showWorkPermitQuestion,
  viewerId,
  visibleLanguageQuestionIds,
}: Props): ((answers: PreScreeningAnswers) => Promise<void>) => {
  const apolloClient = useApolloClient();

  return useCallback(
    async (answers: PreScreeningAnswers): Promise<void> => {
      if (!viewerId) {
        return;
      }

      const answeredLanguages = visibleLanguageQuestionIds.flatMap(
        (questionId) => {
          const answer = answers[questionId];

          if (answer === undefined || answer === '') {
            return [];
          }

          return [
            {
              ...languageQuestionData[questionId],
              skillLevelId: Number(answer),
            },
          ];
        }
      );
      const updatedLanguageByIdentifier = new Map(
        answeredLanguages.map((language) => [language.identifier, language])
      );
      const updatedLanguages = profileLanguages.map((language) => {
        if (!language.identifier) {
          return toLanguageInput(language);
        }

        const updatedLanguage = updatedLanguageByIdentifier.get(
          language.identifier
        );

        return {
          ...toLanguageInput(language),
          ...(updatedLanguage
            ? { skillLevelId: updatedLanguage.skillLevelId }
            : {}),
        };
      });
      const addedLanguages = answeredLanguages.filter(
        (language) =>
          !profileLanguages.some(
            (profileLanguage) =>
              profileLanguage.identifier === language.identifier
          )
      );

      try {
        apolloClient.mutate({
          mutation: UpdateLanguagesAndWorkPermitDocument,
          variables: {
            profileLanguagesUpdateInput: {
              collection: [...updatedLanguages, ...addedLanguages],
              userId: viewerId,
            },
            profileWorkEligibilityUpdateInput: {
              ...(showWorkPermitQuestion
                ? { euWorkPermit: answers.euWorkEligibility === 'yes' }
                : {}),
              profileId: viewerId,
            },
          },
        });
      } finally {
        setShowPreScreeningDialog(false);
        continueApplyFlow();
      }
    },
    [
      apolloClient,
      continueApplyFlow,
      profileLanguages,
      setShowPreScreeningDialog,
      showWorkPermitQuestion,
      viewerId,
      visibleLanguageQuestionIds,
    ]
  );
};
